import { useEffect, useState } from 'react';
import { useMountedRef } from 'hooks/useMountRef';
import { canvasService } from 'services/fileService/canvasService';

export type ImageNaturalDimensions = {
  width: number;
  height: number;
  failed: boolean;
};

const EMPTY_DIMENSIONS: ImageNaturalDimensions = {
  width: 0,
  height: 0,
  failed: false,
};

const IMAGE_LOAD_MAX_ATTEMPTS = 2;
const IMAGE_LOAD_RETRY_DELAY_MS = 300;

export async function loadImageDimensions(
  imageSrc: string
): Promise<{ width: number; height: number }> {
  let lastError: unknown;

  for (let attempt = 0; attempt < IMAGE_LOAD_MAX_ATTEMPTS; attempt += 1) {
    try {
      const image = await canvasService.loadImageFromSource(imageSrc);
      const dimensions = {
        width: image.naturalWidth,
        height: image.naturalHeight,
      };
      canvasService.releaseImageElement(image);
      return dimensions;
    } catch (error) {
      lastError = error;
      if (attempt < IMAGE_LOAD_MAX_ATTEMPTS - 1) {
        await new Promise((resolve) => {
          setTimeout(resolve, IMAGE_LOAD_RETRY_DELAY_MS * (attempt + 1));
        });
      }
    }
  }

  throw lastError;
}

/**
 * Loads image metadata (natural size) for layout before rendering an <img>.
 * Uses canvasService.loadImageFromSource and releases decoded bitmap on cleanup.
 */
export function useImageNaturalDimensions(
  imageSrc: string | undefined
): ImageNaturalDimensions {
  const mountedRef = useMountedRef();
  const [dimensions, setDimensions] =
    useState<ImageNaturalDimensions>(EMPTY_DIMENSIONS);

  useEffect(() => {
    if (!imageSrc) {
      setDimensions(EMPTY_DIMENSIONS);
      return;
    }

    let cancelled = false;

    setDimensions(EMPTY_DIMENSIONS);

    loadImageDimensions(imageSrc)
      .then(({ width, height }) => {
        if (cancelled || !mountedRef.current) {
          return;
        }

        setDimensions({ width, height, failed: false });
      })
      .catch(() => {
        if (!cancelled && mountedRef.current) {
          setDimensions({ width: 0, height: 0, failed: true });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [imageSrc, mountedRef]);

  return dimensions;
}
